'use client';

import { Button } from '@/components/ui';
import { motion } from '@/libs';
import type { PromotionOffer } from '@/utils';

import { UpsellOfferCard } from './components/UpsellOfferCard';
import { type UpsellVariant, usePostGameUpsellModal } from './hooks';

type PostGameUpsellModalProps = {
  raffleId: string;
  raffleName: string;
  offer: PromotionOffer;
  ticketPrice: number;
  gameTitle: string;
  variant?: UpsellVariant;
  onClose: () => void;
  onDismiss?: () => void;
};

const FALLBACK_SUBTITLE: Record<UpsellVariant, string> = {
  post_game: 'Mas você ainda está concorrendo ao sorteio principal!',
  pre_game: 'Garanta mais números com desconto agora, sem perder a sua compra.',
};

export const PostGameUpsellModal = ({
  raffleId,
  raffleName,
  offer,
  ticketPrice,
  gameTitle,
  variant = 'post_game',
  onClose,
  onDismiss,
}: PostGameUpsellModalProps) => {
  const { price, fullPrice, handleAccept, handleDecline } = usePostGameUpsellModal({
    raffleId,
    raffleName,
    offer,
    ticketPrice,
    variant,
    onClose,
    onDismiss,
  });

  const modalSubtitle = offer.description ?? FALLBACK_SUBTITLE[variant];

  return (
    <div
      data-cy="post-game-upsell-modal"
      className="w-full relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 md:p-8 overflow-visible"
      style={{ boxShadow: '0 8px 30px rgba(2,6,23,0.6)' }}
      onClick={(e) => e.stopPropagation()}
    >
      <motion.div
        className="flex flex-col items-center gap-3 w-full relative z-20 mb-5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <h2 className="text-3xl md:text-4xl font-semibold text-primary-foreground uppercase text-center">
          {gameTitle}
        </h2>
        <p className="text-sm text-primary-foreground/80 text-center">{modalSubtitle}</p>
      </motion.div>

      <motion.div
        className="w-full relative z-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <UpsellOfferCard
          offer={offer}
          price={price}
          fullPrice={fullPrice}
          onAccept={handleAccept}
        />
      </motion.div>

      <div className="flex justify-center w-full relative z-20 mt-4">
        <Button
          data-cy="post-game-upsell-decline"
          data-testid="post-game-upsell-decline"
          variant="ghost"
          onClick={handleDecline}
          className="text-primary-foreground/70 text-sm h-9"
        >
          Agora não
        </Button>
      </div>
    </div>
  );
};
